/**
 * FitBoundsOnMount — Reader Polish Prompt 4.
 *
 * Imperative effect child of a `<Map>` from `@vis.gl/react-google-maps`.
 * Computes the union bounding box of the site boundary + every layer
 * geometry and calls `map.fitBounds` once on mount with ~10% padding.
 * Returns null — the side effect is the only output.
 *
 * Skipping logic:
 *   - When `computeSiteMapBounds` returns null (no usable geometry),
 *     the helper does nothing and the parent's `defaultCenter` /
 *     `defaultZoom` carry the day.
 *   - When the bounds collapse to a single point (only the centroid
 *     contributed), we still fitBounds but with a wider pad so the
 *     map doesn't zoom to the maximum street-level zoom.
 */
import { useEffect } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { computeSiteMapBounds, padBounds, type BoundsRect } from '../../lib/siteMapBounds';
import type { SiteEntity } from '../../types/site';

export interface FitBoundsOnMountProps {
  site: Pick<SiteEntity, 'siteCentroid' | 'location' | 'siteBoundary' | 'siteLayers'>;
  /** Padding fraction. Default 0.1 (10%). */
  paddingFraction?: number;
  /** When true, skip fitBounds (e.g. restoring a saved operator viewport). */
  skipFit?: boolean;
  /**
   * Exclude `line`-type layers (pipelines / transmission lines) from the
   * fit bbox so a long pipeline tail doesn't zoom the map out to the whole
   * region. The lines still render as overlays. Defaults to false.
   */
  excludeLineLayers?: boolean;
  /**
   * Pixel padding passed to `map.fitBounds` — breathing room between the
   * fitted geometry and the container edges, on top of the fractional
   * bounds pad. Default 24px.
   */
  fitPaddingPx?: number;
  /**
   * MP (multi-parcel) — every parcel of a multi-parcel site. When provided
   * and non-empty, the union of ALL parcel polygons drives the fit bbox
   * instead of the legacy `site.siteBoundary` (which is only
   * `siteBoundaries[0].polygon` and would frame just the first parcel).
   * Omitted / empty ⇒ falls back to `site.siteBoundary` — single-parcel and
   * legacy behaviour is byte-stable (a single parcel's polygon equals
   * `site.siteBoundary`). Callers should memoise the array so the fit fires
   * only when the parcels actually change.
   */
  parcels?: ReadonlyArray<{ polygon: Array<{ lat: number; lng: number }> }>;
}

export function FitBoundsOnMount({
  site,
  paddingFraction = 0.1,
  skipFit = false,
  excludeLineLayers = false,
  fitPaddingPx = 24,
  parcels,
}: FitBoundsOnMountProps) {
  const map = useMap();

  useEffect(() => {
    if (skipFit || !map || typeof google === 'undefined' || !google.maps) return;

    // Union of every parcel when multi-parcel; else the legacy single
    // boundary. Fitting only `site.siteBoundary` would frame just the first
    // parcel and clip disjoint holdings off-screen.
    const boundary = parcels && parcels.length > 0
      ? parcels.flatMap((p) => p.polygon)
      : site.siteBoundary;

    const rect = computeSiteMapBounds(
      {
        centroid: site.siteCentroid ?? site.location,
        boundary,
        layers: site.siteLayers,
      },
      { excludeLineLayers },
    );
    if (!rect) return;

    // Detect a degenerate point bounds — pad it more aggressively so
    // we don't zoom to the maximum street level.
    const isPoint = rect.north === rect.south && rect.east === rect.west;
    const padded: BoundsRect = padBounds(rect, isPoint ? 0.5 : paddingFraction);

    const sw = new google.maps.LatLng(padded.south, padded.west);
    const ne = new google.maps.LatLng(padded.north, padded.east);
    const bounds = new google.maps.LatLngBounds(sw, ne);
    // Pixel padding keeps parcels off the container edge even when the
    // fractional pad rounds to a near-zero span (tiny single parcel).
    map.fitBounds(bounds, fitPaddingPx);
    // Intentionally fire only on first mount + when the site changes.
    // Subsequent map gestures (pan/zoom) MUST NOT trigger re-fits.
  }, [
    map,
    site.siteCentroid,
    site.location,
    site.siteBoundary,
    site.siteLayers,
    parcels,
    paddingFraction,
    skipFit,
    excludeLineLayers,
    fitPaddingPx,
  ]);

  return null;
}

/**
 * Re-triggers `google.maps` layout when the map container resizes.
 * Prevents horizontally repeating tiles on ultrawide layouts where
 * the map initializes before the aspect-ratio frame settles.
 */
export function MapResizeOnContainer() {
  const map = useMap();

  useEffect(() => {
    if (!map || typeof google === 'undefined' || !google.maps) return;

    const container = map.getDiv()?.parentElement ?? map.getDiv();
    if (!container) return;

    const notifyResize = () => {
      google.maps.event.trigger(map, 'resize');
    };

    notifyResize();
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => notifyResize())
      : null;
    ro?.observe(container);
    window.addEventListener('resize', notifyResize);

    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', notifyResize);
    };
  }, [map]);

  return null;
}
